import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

export default function ProductModal({ product, onClose }) {
  const { addItem } = useCart();
  const [size, setSize] = useState(null);

  useEffect(() => {
    setSize(product ? (product.sizes.includes("M") ? "M" : product.sizes[0]) : null);
  }, [product]);

  const handleAdd = () => {
    addItem(product, size);
    toast.success(`${product.name} (${size}) added to bag`);
    onClose();
  };

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-end md:items-center justify-center p-0 md:p-8"
          data-testid="product-modal"
        >
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-4xl bg-[#111114] border border-white/10 grid md:grid-cols-2 max-h-[92vh] overflow-y-auto"
          >
            <button
              data-testid="product-modal-close"
              onClick={onClose}
              aria-label="Close product view"
              className="absolute top-4 right-4 z-10 p-2 bg-[#0A0A0C]/80 text-white/70 hover:text-[#D3FF24] transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="aspect-[3/4] md:aspect-auto md:min-h-[560px]">
              <img src={product.image} alt={product.name} className="h-full w-full object-cover" />
            </div>
            <div className="p-7 md:p-10 flex flex-col">
              <p className="text-[10px] tracking-[0.3em] text-[#D3FF24] uppercase">
                {product.category} — {product.subcategory}
              </p>
              <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-3 leading-none">
                {product.name}
              </h2>
              <p className="text-sm text-white/55 leading-relaxed mt-5">{product.description}</p>

              <div className="mt-8">
                <p className="text-[10px] tracking-[0.3em] text-white/40 mb-3">SELECT SIZE</p>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((s) => (
                    <button
                      key={s}
                      data-testid={`size-option-${s.toLowerCase().replace(/\s/g, "-")}`}
                      onClick={() => setSize(s)}
                      className={`min-w-12 px-3 py-2.5 text-xs font-bold tracking-widest border transition-colors duration-200 ${
                        size === s
                          ? "bg-[#D3FF24] text-[#0A0A0C] border-[#D3FF24]"
                          : "border-white/15 text-white/70 hover:border-white/40"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-auto pt-10 flex items-center justify-between gap-4">
                <span className="font-display font-black text-3xl text-[#D3FF24]">
                  ${product.price.toFixed(2)}
                </span>
                <button
                  data-testid="add-to-cart-button"
                  onClick={handleAdd}
                  className="flex-1 inline-flex items-center justify-center gap-3 bg-[#D3FF24] text-[#0A0A0C] px-6 py-4 text-xs font-bold tracking-[0.25em] hover:bg-[#BEE61B] transition-colors duration-300"
                >
                  <ShoppingBag className="h-4 w-4" /> ADD TO BAG
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
